import { useEffect, useRef, useState, useCallback } from "react";
import { throttle } from "lodash";
import { getAnalyticsClientId, logAnalytics } from "./analytics";

const MIN_TIME_THRESHOLD_MS = 1000;
const THROTTLE_DELAY = 200;

type SectionState = {
  isActive: boolean;
  startTime: number | null;
  totalElapsed: number;
  lastVisibleTime: number | null;
};

const SectionTracker: React.FC<
  {
    sectionName: string;
    children: React.ReactNode;
    onEnter?: () => void;
  } & React.HTMLAttributes<HTMLDivElement>
> = ({ onEnter, sectionName, children, ...args }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const observersRef = useRef<{
    enter: IntersectionObserver | null;
    leave: IntersectionObserver | null;
  }>({ enter: null, leave: null });

  // Combined state in single ref
  const stateRef = useRef<SectionState>({
    isActive: false,
    startTime: null,
    totalElapsed: 0,
    lastVisibleTime: null,
  });

  // Single cleanup function
  const cleanupObservers = useCallback((type: "enter" | "leave") => {
    if (observersRef.current[type] && sectionRef.current) {
      observersRef.current[type]?.unobserve(sectionRef.current);
      observersRef.current[type]?.disconnect();
      observersRef.current[type] = null;
    }
  }, []);

  // Unified visibility handler
  const handleVisibilityChange = useCallback(() => {
    const now = Date.now();
    const state = stateRef.current;

    if (document.hidden) {
      // Tab hidden: pause timing
      if (state.isActive && state.startTime) {
        state.totalElapsed += now - state.startTime;
        state.startTime = null;
      }
    } else {
      // Tab visible: resume timing
      if (state.isActive && !state.startTime) {
        state.startTime = now;
      }
    }
  }, []);

  // Throttled handlers with useCallback
  const handleSectionEnter = useCallback(
    throttle(() => {
      const state = stateRef.current;

      if (!state.isActive && !document.hidden) {
        state.isActive = true;
        state.startTime = Date.now();
        console.log(`[ANALYTICS] Section enter: ${sectionName}`);

        if (sectionRef.current) {
          observersRef.current.leave = new IntersectionObserver(
            ([entry]) => {
              const rect = entry.boundingClientRect;
              const isFullyOut =
                rect.bottom <= 0 || rect.top >= window.innerHeight;

              if (isFullyOut) {
                handleSectionLeave();
                cleanupObservers("leave");
              }
            },
            { rootMargin: "-1px 0px" }
          );
          observersRef.current.leave.observe(sectionRef.current);
        }

        onEnter?.();
      }
    }, THROTTLE_DELAY),
    [sectionName, cleanupObservers]
  );

  const handleSectionLeave = useCallback(
    throttle(() => {
      const state = stateRef.current;

      if (state.isActive) {
        const finalTime = state.startTime
          ? Date.now() - state.startTime + state.totalElapsed
          : state.totalElapsed;

        if (finalTime >= MIN_TIME_THRESHOLD_MS) {
          console.log(
            `[ANALYTICS] Section time spent: ${sectionName}, ${finalTime}ms`
          );
          logAnalytics("section_time_spent", {
            section: sectionName,
            page: window.location.pathname,
            time_spent_seconds: finalTime / 1000,
            clientId: getAnalyticsClientId(),
          });
        }

        // Reset state
        state.isActive = false;
        state.startTime = null;
        state.totalElapsed = 0;
      }
    }, THROTTLE_DELAY),
    [sectionName]
  );

  // Observer setup
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const sectionHeight = section.offsetHeight;
    const threshold = sectionHeight > window.innerHeight ? 0 : 0.5;

    observersRef.current.enter = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && handleSectionEnter(),
      { threshold }
    );
    observersRef.current.enter.observe(section);

    return () => {
      cleanupObservers("enter");
      cleanupObservers("leave");
      handleSectionLeave();
    };
  }, [sectionName, handleSectionEnter, handleSectionLeave, cleanupObservers]);

  // Visibility listener
  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [handleVisibilityChange]);

  return (
    <div {...args} ref={sectionRef}>
      {children}
    </div>
  );
};

export default SectionTracker;
